// Industry classification form fields — Accounts-01.
//
// Drives sector + 6-digit code + risk class together so the parent
// form (AccountForm) can pin the sector validation in one place.
// Selecting Unknown disables the 6-digit picker and clears any
// prior selection. Changing sector also clears a previously-selected
// 6-digit code (with a one-time toast through ``onCodeCleared``).

import { useEffect, useMemo, useRef, useState } from "react";

import {
  useNaicsSearchQuery,
  useNaicsSectorsQuery,
} from "./hooks";
import type { AccountRiskClass, NaicsSearchResult } from "./types";


export interface IndustryClassificationProps {
  /** Selected sector code (``"54"`` / ``"31-33"`` / ``"unknown"``).
   * Empty string means "not yet set"; the parent form treats that
   * as a validation failure when the user submits. */
  sector: string;
  /** Selected 6-digit code or "" when not set. */
  code: string;
  /** Cached human label for the selected code so the chip renders
   * without a refetch. */
  codeTitle: string;
  riskClass: "" | AccountRiskClass;
  onChange: (next: {
    sector?: string;
    code?: string;
    codeTitle?: string;
    riskClass?: "" | AccountRiskClass;
  }) => void;
  /** Fired once when changing the sector clears a previously-selected
   * 6-digit code. The parent shows a toast. */
  onCodeCleared?: () => void;
  disabled?: boolean;
}


const RISK_OPTIONS: ReadonlyArray<{
  value: AccountRiskClass;
  label: string;
  description: string;
}> = [
  {
    value: "office",
    label: "Office",
    description: "Professional, desk-based",
  },
  {
    value: "mixed",
    label: "Mixed",
    description: "Office plus field work",
  },
  {
    value: "manual",
    label: "Manual",
    description: "Industrial, physical labor",
  },
];


export function IndustryClassification({
  sector, code, codeTitle, riskClass,
  onChange, onCodeCleared, disabled = false,
}: IndustryClassificationProps) {
  const sectorsQuery = useNaicsSectorsQuery();
  const sectors = sectorsQuery.data?.sectors ?? [];

  return (
    <fieldset
      data-testid="account-industry-classification"
      className="flex flex-col gap-3"
      disabled={disabled}
    >
      <Field label="Industry sector" required>
        <select
          data-testid="account-industry-sector"
          value={sector}
          onChange={(e) => {
            const nextSector = e.target.value;
            const cleared =
              code !== "" && nextSector !== sector;
            onChange({
              sector: nextSector,
              ...(cleared || nextSector === "unknown"
                ? { code: "", codeTitle: "" }
                : {}),
            });
            if (cleared && onCodeCleared) onCodeCleared();
          }}
          className={INPUT_CLASS}
        >
          <option value="">Select…</option>
          {sectors.map((s) => (
            <option key={s.code} value={s.code}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Specific industry code" hint="optional">
        <SpecificCodePicker
          sector={sector}
          code={code}
          codeTitle={codeTitle}
          disabled={
            sector === "" || sector === "unknown"
          }
          onPick={(picked) => {
            onChange({
              code: picked.code,
              codeTitle: picked.title,
            });
          }}
          onClear={() => onChange({ code: "", codeTitle: "" })}
        />
        {sector === "unknown" && (
          <p
            data-testid="account-industry-unknown-helper"
            className="text-[11px] text-text-tertiary"
          >
            Specific industry code isn&rsquo;t available when the
            sector is Unknown.
          </p>
        )}
      </Field>

      <Field label="Industry risk class" hint="optional">
        <div
          data-testid="account-industry-risk-class"
          role="radiogroup"
          aria-label="Industry risk class"
          className="grid grid-cols-3 gap-2"
        >
          {RISK_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              data-testid={`account-industry-risk-${opt.value}`}
              className={[
                "flex cursor-pointer flex-col rounded-md border px-3 py-2 text-xs",
                riskClass === opt.value
                  ? "border-teal bg-sage-tint/40"
                  : "border-border-subtle hover:border-border-strong",
              ].join(" ")}
            >
              <input
                type="radio"
                name="risk_class"
                value={opt.value}
                checked={riskClass === opt.value}
                onChange={() => onChange({ riskClass: opt.value })}
                className="sr-only"
              />
              <span className="text-sm font-medium text-text-primary">
                {opt.label}
              </span>
              <span className="text-[11px] text-text-tertiary">
                {opt.description}
              </span>
            </label>
          ))}
        </div>
        {riskClass !== "" && (
          <button
            type="button"
            data-testid="account-industry-risk-clear"
            onClick={() => onChange({ riskClass: "" })}
            className="self-start text-[11px] text-text-tertiary hover:text-text-primary"
          >
            Clear
          </button>
        )}
      </Field>
    </fieldset>
  );
}


function SpecificCodePicker({
  sector, code, codeTitle, disabled, onPick, onClear,
}: {
  sector: string;
  code: string;
  codeTitle: string;
  disabled: boolean;
  onPick: (row: { code: string; title: string }) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(query), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Reset query when sector changes so the previous results don't
  // bleed into the new sector's picker.
  useEffect(() => {
    setQuery("");
    setDebounced("");
  }, [sector]);

  const search = useNaicsSearchQuery({
    q: debounced,
    sector: sector === "unknown" || sector === "" ? undefined : sector,
    limit: 10,
  });
  const results = useMemo(
    () => search.data?.results ?? [],
    [search.data],
  );

  if (code) {
    return (
      <div
        data-testid="account-industry-code-chip"
        className="inline-flex items-center gap-2 rounded-md border border-border-subtle bg-canvas/40 px-3 py-2"
      >
        <span className="text-xs font-medium text-text-primary">
          {code} — {codeTitle}
        </span>
        <button
          type="button"
          data-testid="account-industry-code-clear"
          onClick={onClear}
          disabled={disabled}
          className="text-[11px] text-text-tertiary hover:text-ember-ink disabled:opacity-50"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <input
        type="text"
        data-testid="account-industry-code-search"
        value={query}
        placeholder={
          disabled
            ? "Pick an industry sector first"
            : "Type to search 6-digit NAICS codes…"
        }
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        className={INPUT_CLASS}
      />
      {!disabled && debounced.trim().length >= 2 && (
        <ul
          role="listbox"
          data-testid="account-industry-code-results"
          className="max-h-56 overflow-y-auto rounded-md border border-border-subtle bg-raised shadow-sm"
        >
          {search.isPending ? (
            <li className="px-3 py-2 text-xs text-text-tertiary">
              Searching…
            </li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-xs text-text-tertiary">
              No NAICS codes match.
            </li>
          ) : (
            results.map((row) => (
              <li key={row.code}>
                <button
                  type="button"
                  data-testid={`account-industry-code-option-${row.code}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPick({ code: row.code, title: row.title });
                  }}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-xs hover:bg-recessed"
                >
                  <span className="font-medium text-text-primary">
                    {row.code} — {row.title}
                  </span>
                  {row.description_snippet && (
                    <span className="line-clamp-2 text-[11px] text-text-tertiary">
                      {row.description_snippet}
                    </span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}


function Field({
  label, required = false, hint, children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-text-secondary">
      <span>
        {label}
        {required && <span className="text-ember-ink"> *</span>}
        {hint && (
          <span className="ml-1 text-text-tertiary">({hint})</span>
        )}
      </span>
      {children}
    </label>
  );
}


const INPUT_CLASS =
  "w-full rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30";


// Provide a small label helper for the IndustryCard (detail view).
export function sectorLabelForCode(
  code: string | null | undefined,
  sectors: NaicsSearchResult[] | { sectors: { code: string; label: string }[] } | undefined,
): string | null {
  if (!code) return null;
  const list = Array.isArray(sectors) ? [] : sectors?.sectors ?? [];
  const found = list.find((s) => s.code === code);
  return found ? found.label : code;
}
